"use client";

import { createContext, type ReactNode, useContext, useMemo } from "react";
import type { ModuleInteractionTargetKind } from "@/lib/module-interaction";
import type { HoverSelectOrigin } from "@/lib/hooks/use-hover-select";

export interface ChartSelectSource {
  /** The clicked element, used to attribute the interaction to its module. */
  element?: Element | null;
  /** Which kind of chart geometry was operated. */
  kind?: ModuleInteractionTargetKind;
  /** Stable id of the series or datum, when the chart has one. */
  id?: string | number | null;
}

interface ChartLegendHoverContextValue {
  hoveredIndex: number | null;
  setHoveredIndex: (index: number | null) => void;
  /**
   * The standing legend selection, whatever surface wrote it. Not what chart
   * geometry marks itself by — that is `elementIndex` — and not what a filter
   * reads, which is `filterIndex`.
   */
  selectedIndex: number | null;
  /**
   * What a tap on chart geometry selected. Its own slot, held apart from
   * `selectedIndex` so a tap can neither mark the legend row nor drop a
   * standing legend filter.
   */
  elementIndex: number | null;
  /**
   * The only value a filter may read — a dim, or a tooltip reduced to one row.
   * Hover is legend-owned and previews a filter; a standing selection counts
   * only when it was made at the legend, so a tap on chart geometry changes
   * nothing about what stays visible.
   */
  filterIndex: number | null;
  toggleSelectedIndex: (index: number, source?: ChartSelectSource) => void;
}

const ChartLegendHoverContext =
  createContext<ChartLegendHoverContextValue | null>(null);

export function ChartLegendHoverProvider({
  hoveredIndex,
  onHoverChange,
  selectedIndex = null,
  selectedOrigin = null,
  elementIndex,
  onElementToggle,
  children,
}: {
  hoveredIndex: number | null;
  onHoverChange: (index: number | null) => void;
  selectedIndex?: number | null;
  /** Which surface `selectedIndex` was made on. Without it nothing filters. */
  selectedOrigin?: HoverSelectOrigin | null;
  /** The element slot of `useLegendClickSync`. Required so a tap has a place
   * to land that is not the legend's. */
  elementIndex: number | null;
  onElementToggle: (index: number, source?: ChartSelectSource) => void;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({
      hoveredIndex,
      setHoveredIndex: onHoverChange,
      selectedIndex,
      elementIndex,
      filterIndex:
        hoveredIndex ?? (selectedOrigin === "legend" ? selectedIndex : null),
      toggleSelectedIndex: onElementToggle,
    }),
    [
      hoveredIndex,
      onHoverChange,
      selectedIndex,
      selectedOrigin,
      elementIndex,
      onElementToggle,
    ]
  );

  return (
    <ChartLegendHoverContext.Provider value={value}>
      {children}
    </ChartLegendHoverContext.Provider>
  );
}

export function useChartLegendHover(): ChartLegendHoverContextValue {
  const context = useContext(ChartLegendHoverContext);
  return (
    context ?? {
      hoveredIndex: null,
      setHoveredIndex: () => {
        /* noop outside ChartLegendHoverProvider */
      },
      selectedIndex: null,
      elementIndex: null,
      filterIndex: null,
      toggleSelectedIndex: () => {
        /* noop outside ChartLegendHoverProvider */
      },
    }
  );
}
